import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { FIELD_DEFS, registerError, toPayload, validateEntry } from '@/lib/auditRegisters';
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';
import { roleLabel } from '@/lib/roleLabels';

function NativeSelect({ id, value, onChange, options, placeholder = '— select —', ...rest }) {
  return (
    <select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" {...rest}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

function Field({ f, value, onChange, error, branches, roles, staff }) {
  const id = `reg-field-${f.key}`; const errId = `${id}-error`;
  const common = { id, 'aria-invalid': !!error, 'aria-describedby': error ? errId : undefined, 'aria-required': !!f.required, 'data-testid': `register-field-${f.key}` };
  let control;
  if (f.type === 'select') control = <NativeSelect {...common} value={value} onChange={onChange} options={f.options} />;
  else if (f.type === 'branch') control = <NativeSelect {...common} value={value} onChange={onChange} options={branches} placeholder="— any branch —" />;
  else if (f.type === 'role') control = <NativeSelect {...common} value={value} onChange={onChange} options={roles} placeholder="— select role —" />;
  else if (f.type === 'staff') {
    const { 'data-testid': tid, ...aria } = common;
    control = <SearchSelect options={staff} value={value ?? ''} onChange={onChange} placeholder="— select staff member —" searchPlaceholder={STAFF_SEARCH_HINT} emptyText="No staff match that search" clearLabel="— select staff member —" testId={tid} triggerProps={aria} />;
  }
  else if (f.type === 'textarea') control = <Textarea {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={3} />;
  else if (f.type === 'multi') control = (
    <div className="flex flex-wrap gap-3" role="group" aria-labelledby={`${id}-label`} data-testid={`register-field-${f.key}`}>
      {f.options.map((o) => (
        <label key={o} className="inline-flex items-center gap-2 text-sm">
          <Checkbox checked={(value || []).includes(o)} onCheckedChange={(c) => onChange(c ? [...(value || []), o] : (value || []).filter((x) => x !== o))} aria-label={o} /> {o}
        </label>
      ))}
    </div>
  );
  else if (f.type === 'source_ref') control = (
    <div className="grid grid-cols-3 gap-2">
      <NativeSelect id={id} value={value?.kind || 'external'} onChange={(k) => onChange({ ...(value || {}), kind: k })} options={['external', 'feedback', 'incident', 'form_submission']} placeholder="kind" aria-label="Source kind" data-testid="register-field-source_ref-kind" />
      <Input className="col-span-2" value={value?.id_or_ref || ''} onChange={(e) => onChange({ kind: value?.kind || 'external', id_or_ref: e.target.value })} aria-label="Source reference (text identifier, no URLs)" data-testid="register-field-source_ref-ref" placeholder="Record ID or text reference" />
    </div>
  );
  else control = <Input {...common} type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} min={f.min} max={f.max} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  return (
    <div className="space-y-1">
      <Label htmlFor={id} id={`${id}-label`}>{f.label}{f.required && <span aria-hidden="true"> *</span>}</Label>
      {control}
      {error && <p id={errId} role="alert" className="text-sm text-red-700" data-testid={`register-error-${f.key}`}>{error}</p>}
    </div>
  );
}

export function RegisterEntryDialog({ open, onOpenChange, type, register, entry, branches, onSaved }) {
  const defs = FIELD_DEFS[type] || [];
  const [values, setValues] = useState({}); const [errors, setErrors] = useState({}); const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false); const [confirmDup, setConfirmDup] = useState(false); const [status, setStatus] = useState('');
  const [roles, setRoles] = useState([]); const [staff, setStaff] = useState([]); const summaryRef = useRef(null);
  const needsRoles = defs.some((f) => f.type === 'role'), needsStaff = defs.some((f) => f.type === 'staff');

  useEffect(() => {
    if (!open) return;
    const init = {}; defs.forEach((f) => { init[f.key] = entry ? entry[f.key] ?? (f.type === 'multi' ? [] : '') : (f.type === 'multi' ? [] : ''); });
    if (entry?.source_ref) init.source_ref = entry.source_ref;
    setValues(init); setErrors({}); setServerError(null); setConfirmDup(false); setStatus(entry?.status || '');
    if (needsRoles) api.get('/admin/roles').then(({ data }) => setRoles((data.items || data.data || data || []).map((r) => ({ value: r.id || r._id, label: roleLabel(r.name) })))).catch(() => setRoles([]));
    if (needsStaff) fetchAllPages('/staff').then((items) => setStaff((items || []).map((s) => ({ value: s.id || s._id, label: s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.employee_id || s.id })))).catch(() => setStaff([]));
  }, [open, entry, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorList = useMemo(() => Object.entries(errors), [errors]);
  const set = (k) => (v) => { setValues((s) => ({ ...s, [k]: v })); setErrors((e) => { const n = { ...e }; delete n[k]; return n; }); };

  const submit = async (e) => {
    e.preventDefault(); setServerError(null);
    const errs = validateEntry(type, values);
    if (Object.keys(errs).length) { setErrors(errs); setTimeout(() => summaryRef.current?.focus(), 0); return; }
    setSaving(true);
    try {
      const payload = toPayload(type, values);
      let res;
      if (entry) res = await api.put(`/audit/registers/${type}/${entry.id}`, { ...payload, revision: entry.revision, ...(status && status !== entry.status ? { status } : {}) });
      else res = await api.post(`/audit/registers/${type}`, { ...payload, ...(confirmDup ? { confirm_duplicate: true } : {}) });
      onSaved?.(res.data.data); onOpenChange(false);
    } catch (err) {
      const er = registerError(err);
      if (er.code === 'REGISTER_DUPLICATE' && er.details?.confirmable) setConfirmDup(true);
      if (er.code === 'VALIDATION_ERROR' && er.details?.errors) { const fe = {}; er.details.errors.forEach((x) => { fe[x.loc[x.loc.length - 1]] = x.msg; }); setErrors(fe); }
      setServerError(er); setTimeout(() => summaryRef.current?.focus(), 0);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="register-entry-dialog">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>{entry ? `Edit ${entry.register_no}` : `Add entry — ${register?.label || type}`}</DialogTitle>
            <DialogDescription>Fields marked * are required. Reference fields accept text identifiers only — never URLs, tokens or copied sensitive content.</DialogDescription>
          </DialogHeader>
          {(errorList.length > 0 || serverError) && (
            <div ref={summaryRef} tabIndex={-1} role="alert" aria-labelledby="register-error-summary-title" className="my-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm" data-testid="register-error-summary">
              <p id="register-error-summary-title" className="font-medium">{serverError ? serverError.message : 'Please fix the following before saving:'}</p>
              {serverError?.code && <p className="text-xs font-mono mt-1" data-testid="register-error-summary-code">{serverError.code}</p>}
              {serverError?.code === 'REGISTER_REVISION_CONFLICT' && <p className="mt-1">This entry was changed by someone else (now revision {serverError.details?.current_revision}). Close this dialog, reload the entry and retry.</p>}
              {serverError?.code === 'REGISTER_DUPLICATE' && <p className="mt-1">Existing entry: {serverError.details?.existing_register_no}. {serverError.details?.confirmable ? 'Tick “Save anyway” below to record a deliberate duplicate.' : 'Continuous Improvement allows one entry per source record.'}</p>}
              {errorList.length > 0 && <ul className="list-disc ml-5 mt-1">{errorList.map(([k, m]) => <li key={k}><a href={`#reg-field-${k}`} className="underline" onClick={(ev) => { ev.preventDefault(); document.getElementById(`reg-field-${k}`)?.focus(); }}>{m}</a></li>)}</ul>}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 py-2">
            {defs.map((f) => <div key={f.key} className={f.type === 'textarea' || f.type === 'multi' || f.type === 'source_ref' ? 'sm:col-span-2' : ''}><Field f={f} value={values[f.key]} onChange={set(f.key)} error={errors[f.key]} branches={branches} roles={roles} staff={staff} /></div>)}
            {entry && register?.statuses?.length > 0 && (
              <div className="space-y-1"><Label htmlFor="reg-field-status">Status</Label>
                <NativeSelect id="reg-field-status" value={status} onChange={setStatus} options={register.statuses} placeholder="— keep current —" data-testid="register-field-status" /></div>
            )}
          </div>
          {confirmDup && (
            <label className="flex items-center gap-2 text-sm my-2"><Checkbox checked={confirmDup} onCheckedChange={(c) => setConfirmDup(!!c)} data-testid="register-confirm-duplicate" /> Save anyway (record a deliberate duplicate)</label>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="register-entry-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="register-entry-save">{saving ? 'Saving…' : entry ? 'Save changes' : 'Create entry'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
